import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Phone, LogIn, LayoutDashboard, Menu, X } from "lucide-react";
import { useSiteContent } from "@/hooks/useContent";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_LOGO = "/logo-transparent.png";

const BASE_LINKS = [
  { label: "About", href: "/about" },
  { label: "Credentials", href: "/qualifications" },
  { label: "Training", href: "/personal-training" },
  { label: "Nutrition", href: "/nutrition" },
  { label: "Online", href: "/online-training" },
  { label: "Reviews", href: "/reviews" },
  { label: "Results", href: "/transformations" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hasProducts, setHasProducts] = useState(false);
  const [hasGallery, setHasGallery] = useState(false);
  const location = useLocation();
  const { content } = useSiteContent();
  const logo = content?.settings?.logo || FALLBACK_LOGO;
  const wa = content?.settings?.whatsapp || "919153020904";

  const isClientLoggedIn = !!(localStorage.getItem("gg_client_token") || sessionStorage.getItem("gg_client_token"));
  const isTrainerLoggedIn = !!(localStorage.getItem("gg_admin_token") || sessionStorage.getItem("gg_admin_token"));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setHasProducts(r.data.length > 0)).catch(() => {});
    axios.get(`${API}/public/gallery`).then((r) => setHasGallery(r.data.length > 0)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navLinks = (() => {
    let links = [...BASE_LINKS];
    if (hasGallery) {
      // Insert Gallery right after Reviews (index 5 in BASE_LINKS = "Reviews")
      const reviewIdx = links.findIndex((l) => l.href === "/reviews");
      links.splice(reviewIdx + 1, 0, { label: "Gallery", href: "/gallery" });
    }
    if (hasProducts) {
      const idx = links.findIndex((l) => l.href === "/transformations");
      links.splice(idx, 0, { label: "Shop", href: "/shop" });
    }
    return links;
  })();

  const authLink = isClientLoggedIn
    ? { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }
    : isTrainerLoggedIn
    ? { to: "/admin", label: "Dashboard", icon: LayoutDashboard }
    : { to: "/login", label: "Login", icon: LogIn };

  return (
    <>
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav py-1 md:py-1.5" : "bg-black/70 backdrop-blur-md py-1.5 md:py-2"}`} data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
          <Link to="/" data-testid="navbar-logo" className="shrink-0"><img src={logo} alt="GG Fitness" className="h-7 sm:h-8 md:h-10" style={{mixBlendMode:'lighten'}} /></Link>

          {/* Desktop nav — visible xl and up */}
          <div className="hidden xl:flex items-center gap-2 xl:gap-3">
            {navLinks.map((link) => (
              <Link key={link.href} to={link.href} data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`font-heading text-[9px] xl:text-[10px] uppercase tracking-wide transition-colors duration-300 relative group whitespace-nowrap ${location.pathname === link.href ? "text-white" : "text-zinc-400 hover:text-white"}`}>
                {link.label}
                <span className={`absolute -bottom-1 left-0 h-[2px] bg-gg-red rounded-full transition-all duration-300 ${location.pathname === link.href ? "w-full" : "w-0 group-hover:w-full"}`} />
              </Link>
            ))}
            <Link to={authLink.to} data-testid="nav-login" className="font-heading text-[9px] xl:text-[10px] uppercase tracking-wide text-zinc-400 hover:text-white transition-colors flex items-center gap-1 whitespace-nowrap">
              <authLink.icon size={10} /> {authLink.label}
            </Link>
            <a href={`tel:+${wa}`} data-testid="nav-call-button" className="font-heading text-[9px] xl:text-[10px] uppercase tracking-wide border border-white/20 text-white px-2.5 py-1.5 rounded-lg hover:border-gg-red hover:text-gg-red transition-all duration-300 flex items-center gap-1 whitespace-nowrap"><Phone size={10} /> Call</a>
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" data-testid="nav-cta-whatsapp" className="font-heading text-[9px] xl:text-[10px] uppercase tracking-wide bg-gg-red text-white px-3 py-1.5 rounded-lg hover:bg-gg-red-hover transition-all duration-300 whitespace-nowrap">Book Now</a>
          </div>

          {/* Hamburger — visible below xl */}
          <button className="xl:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile slide-from-right menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] xl:hidden" onClick={() => setMobileOpen(false)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="fixed top-0 right-0 bottom-0 w-[260px] bg-gg-black border-l border-white/10 z-[60] xl:hidden flex flex-col" data-testid="mobile-menu">
              <div className="px-5 pt-5 pb-3 border-b border-white/5">
                <Link to="/" onClick={() => setMobileOpen(false)}><img src={logo} alt="GG Fitness" className="h-8" /></Link>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-0.5">
                {navLinks.map((link, i) => (
                  <motion.div key={link.href} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i, duration: 0.25 }}>
                    <Link to={link.href} onClick={() => setMobileOpen(false)}
                      className={`block font-heading text-sm uppercase tracking-widest py-2 transition-colors ${location.pathname === link.href ? "text-gg-red" : "text-zinc-300 hover:text-gg-red"}`}>
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                <motion.div initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * navLinks.length, duration: 0.25 }}>
                  <Link to={authLink.to} onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 font-heading text-sm uppercase tracking-widest py-2 text-zinc-300 hover:text-gg-red transition-colors">
                    <authLink.icon size={14} /> {authLink.label}
                  </Link>
                </motion.div>
              </div>
              <div className="px-5 pb-5 space-y-2 border-t border-white/5 pt-3">
                <div className="flex gap-2">
                  <a href={`tel:+${wa}`} className="flex-1 font-heading text-xs uppercase tracking-widest border border-white/20 text-white px-3 py-2.5 rounded-lg text-center flex items-center justify-center gap-1.5"><Phone size={12} /> Call</a>
                  <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="flex-1 font-heading text-xs uppercase tracking-widest bg-gg-red text-white px-3 py-2.5 rounded-lg text-center hover:bg-gg-red-hover transition-colors">Book Now</a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
